// src/components/admin/flows/FlowDashboard.js
import React, { useEffect, useMemo } from 'react';
import {
    GitBranch, Play, CheckCircle, AlertTriangle,
    Clock, TrendingUp, Plus, BookTemplate, ArrowRight, History
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell
} from 'recharts';
import { useFlowAutomation } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import StatusBadge from './components/StatusBadge';

const StatCard = ({ icon: Icon, label, value, color, isDark }) => (
    <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
        <div className="flex items-center gap-3">
            <div className={`p-2.5 rounded-lg ${color}`}>
                <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{label}</p>
                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value}</p>
            </div>
        </div>
    </div>
);

const PIE_COLORS = ['#22c55e', '#ef4444', '#3b82f6', '#6b7280'];

const AnalyticsSection = ({ executions, workflows, isDark }) => {
    // Execution status breakdown for pie chart
    const statusData = useMemo(() => {
        const counts = { completed: 0, failed: 0, running: 0, cancelled: 0 };
        executions.forEach(e => { if (counts[e.status] !== undefined) counts[e.status]++; });
        return Object.entries(counts)
            .filter(([, v]) => v > 0)
            .map(([name, value]) => ({ name, value }));
    }, [executions]);

    // Executions per workflow for bar chart
    const workflowRunData = useMemo(() => {
        const map = {};
        executions.forEach(e => {
            const name = e.workflow_name || 'Unknown';
            map[name] = (map[name] || 0) + 1;
        });
        return Object.entries(map)
            .map(([name, runs]) => ({ name: name.length > 16 ? name.slice(0, 16) + '...' : name, runs }))
            .sort((a, b) => b.runs - a.runs)
            .slice(0, 6);
    }, [executions]);

    if (executions.length === 0) return null;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Status Breakdown */}
            <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
                <h3 className={`font-semibold text-sm mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Execution Status Breakdown
                </h3>
                <div className="flex items-center gap-6">
                    <ResponsiveContainer width={140} height={140}>
                        <PieChart>
                            <Pie
                                data={statusData}
                                cx="50%"
                                cy="50%"
                                innerRadius={35}
                                outerRadius={60}
                                paddingAngle={3}
                                dataKey="value"
                            >
                                {statusData.map((_, i) => (
                                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1f2937' : '#fff',
                                    border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                }}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                    <div className="space-y-2">
                        {statusData.map((item, i) => (
                            <div key={item.name} className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                                <span className={`text-xs capitalize ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {item.name}: {item.value}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Runs per Workflow */}
            <div className={`rounded-xl p-5 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
                <h3 className={`font-semibold text-sm mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Runs by Workflow
                </h3>
                {workflowRunData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={workflowRunData} layout="vertical" margin={{ left: 0, right: 10 }}>
                            <XAxis type="number" hide />
                            <YAxis
                                type="category"
                                dataKey="name"
                                width={100}
                                tick={{ fontSize: 11, fill: isDark ? '#9ca3af' : '#6b7280' }}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: isDark ? '#1f2937' : '#fff',
                                    border: isDark ? '1px solid #374151' : '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                    fontSize: '12px',
                                }}
                            />
                            <Bar dataKey="runs" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={16} />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p className={`text-sm text-center py-8 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        No data yet
                    </p>
                )}
            </div>
        </div>
    );
};

const FlowDashboard = ({ onNavigate }) => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const { stats, executions, workflows, fetchStats, fetchWorkflows, fetchExecutions, loading } = useFlowAutomation();
    const isDark = theme === 'dark';
    const [pendingApprovals, setPendingApprovals] = React.useState([]);

    useEffect(() => {
        let cancelled = false;
        const loadAll = async () => {
            // Fetch all data in parallel
            await Promise.all([
                fetchStats(),
                fetchWorkflows(),
                fetchExecutions(null, { limit: 10 }),
            ]);
            if (cancelled) return;
            // Load pending approvals
            try {
                const mod = await import('../../../services/flowAutomation.service');
                const r = await mod.default.getPendingApprovals();
                if (!cancelled) setPendingApprovals(r.approvals || []);
            } catch {}
        };
        loadAll();
        return () => { cancelled = true; };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const activeWorkflows = workflows.filter(w => w.status === 'active');
    const recentExecutions = executions.slice(0, 8);

    const statCards = [
        { icon: GitBranch, label: 'Active Workflows', value: stats?.active_workflows ?? activeWorkflows.length, color: 'bg-blue-500' },
        { icon: Play, label: 'Total Runs', value: stats?.total_runs ?? 0, color: 'bg-green-500' },
        { icon: CheckCircle, label: 'Success Rate', value: `${stats?.success_rate ?? 0}%`, color: 'bg-emerald-500' },
        { icon: Clock, label: 'Avg Duration', value: `${stats?.avg_duration_hours ?? 0}h`, color: 'bg-purple-500' },
    ];

    return (
        <div className="space-y-6">
            {/* Pending Approvals banner */}
            {pendingApprovals.length > 0 && (
                <div className={`rounded-xl p-4 border-l-4 border-purple-500 ${isDark ? 'bg-purple-900/20' : 'bg-purple-50'}`}>
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-purple-500">
                                <AlertTriangle className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {pendingApprovals.length} workflow {pendingApprovals.length === 1 ? 'approval' : 'approvals'} waiting
                                </p>
                                <p className={`text-xs ${isDark ? 'text-purple-300' : 'text-purple-700'}`}>
                                    Review these in your notifications or bell icon
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Stats Grid */}
            <div className="tour-flow-stats grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {statCards.map((card, i) => (
                    <StatCard key={i} {...card} isDark={isDark} />
                ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <button
                    onClick={() => navigate('/admin/flows/builder')}
                    className={`tour-flow-create flex items-center gap-4 p-5 rounded-xl border-2 border-dashed transition-all hover:shadow-md ${
                        isDark
                            ? 'border-gray-600 hover:border-blue-500 bg-gray-800/50'
                            : 'border-gray-300 hover:border-blue-400 bg-white'
                    }`}
                >
                    <div className="p-3 rounded-lg bg-blue-500">
                        <Plus className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Create Workflow</p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Build a custom automation from scratch
                        </p>
                    </div>
                </button>

                <button
                    onClick={() => onNavigate('templates')}
                    className={`tour-flow-templates flex items-center gap-4 p-5 rounded-xl border-2 border-dashed transition-all hover:shadow-md ${
                        isDark
                            ? 'border-gray-600 hover:border-purple-500 bg-gray-800/50'
                            : 'border-gray-300 hover:border-purple-400 bg-white'
                    }`}
                >
                    <div className="p-3 rounded-lg bg-purple-500">
                        <BookTemplate className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-left">
                        <p className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Browse Templates</p>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            Start with a pre-built workflow template
                        </p>
                    </div>
                </button>
            </div>

            {/* Analytics Charts */}
            <AnalyticsSection executions={executions} workflows={workflows} isDark={isDark} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Active Workflows */}
                <div className={`rounded-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Active Workflows</h3>
                        <button
                            onClick={() => onNavigate('workflows')}
                            className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
                        >
                            View all <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {activeWorkflows.length === 0 ? (
                            <div className={`p-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <GitBranch className="w-8 h-8 mx-auto mb-2 opacity-40" />
                                <p className="text-sm">No active workflows yet</p>
                            </div>
                        ) : (
                            activeWorkflows.slice(0, 5).map((wf) => (
                                <div
                                    key={wf.id}
                                    className={`flex items-center justify-between p-3 cursor-pointer hover:${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}
                                    onClick={() => navigate(`/admin/flows/builder/${wf.id}`)}
                                >
                                    <div className="flex items-center gap-3">
                                        <GitBranch className={`w-4 h-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                                        <div>
                                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{wf.name}</p>
                                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {wf.stats?.runs ?? 0} runs
                                            </p>
                                        </div>
                                    </div>
                                    <StatusBadge status={wf.status} />
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Recent Executions */}
                <div className={`rounded-xl ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
                    <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                        <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Recent Executions</h3>
                        <button
                            onClick={() => onNavigate('history')}
                            className="text-sm text-blue-500 hover:text-blue-600 flex items-center gap-1"
                        >
                            View all <ArrowRight className="w-3 h-3" />
                        </button>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {recentExecutions.length === 0 ? (
                            <div className={`p-8 text-center ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                <History className="w-8 h-8 mx-auto mb-2 opacity-40" />
                                <p className="text-sm">No executions yet</p>
                            </div>
                        ) : (
                            recentExecutions.map((exec) => (
                                <div key={exec.id} className={`flex items-center justify-between p-3`}>
                                    <div className="flex items-center gap-3">
                                        {exec.status === 'completed' ? (
                                            <CheckCircle className="w-4 h-4 text-green-500" />
                                        ) : exec.status === 'failed' ? (
                                            <AlertTriangle className="w-4 h-4 text-red-500" />
                                        ) : (
                                            <Clock className="w-4 h-4 text-amber-500" />
                                        )}
                                        <div>
                                            <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {exec.workflow_name || 'Workflow'}
                                            </p>
                                            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {exec.started_at ? new Date(exec.started_at).toLocaleString() : '—'}
                                            </p>
                                        </div>
                                    </div>
                                    <StatusBadge status={exec.status} />
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FlowDashboard;
